import { Button, Grid, SvgIcon, Typography } from "@mui/material";
import Title from "../../../common/Title";
import PhoneNumbersFormArea from "./PhoneNumbersFormArea";
import { IPhoneObject } from "@/Settings/types/Common/model";
import { useEffect, useState } from "react";
import {
	clearPhonesNumbers,
	convertStringMaskToRegexArray,
	createMutationInput,
	validatePhoneNumbers,
} from "@/Settings/helpers/SystemSettings/callActivity/utilities";
import conformToMask from "@/Settings/helpers/SystemSettings/callActivity/conformToMask";
import { TCountrySelected } from "@/Settings/types/SystemSettings/callActivity";
import { useMutation } from "@tanstack/react-query";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import ActionCard from "@Layouts/components/Cards/ActionCard/ActionCard";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { errorLogger } from "@/Common/helpers/logger";
import CallActivityBlackListDialog from "./CallActivityBlackListDialog";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";

type TCallActivityBlackListProps = {
	locationId: string;
	phoneNumbers?: IPhoneObject[];
};

const color = "var(--gray-blue-color)";

const CallActivityBlackList = (props: TCallActivityBlackListProps) => {
	const { locationId, phoneNumbers = [] } = props;
	const [countrySelected, setCountrySelected] = useState<TCountrySelected | null>(null);
	const [numbers, setNumbers] = useState("");
	const [error, setError] = useState(false);
	const [pressedKey, setPressedKey] = useState<string | null>(null);
	const [toDeletePhoneNumbers, setToDeletePhoneNumbers] = useState<string[]>([]);
	const [openDialog, setOpenDialog] = useState(false);

	const { mutate: mutateAddPhoneNumbers, isPending } = useMutation({
		mutationKey: ["ADD_BLACK_LIST_PHONE_NUMBERS", locationId],
		mutationFn: (input: { blacklistPhoneNumbers: { phoneNumber: string }[] }) => {
			return updateSystemSettings(locationId, input, "BLACK_LIST_PHONE_NUMBERS");
		},
	});
	const { mutate: mutateDeletePhoneNumber, isPending: isDeleting } = useMutation({
		mutationKey: ["DELETE_BLACK_LIST_PHONE_NUMBERS", locationId],
		mutationFn: (input: { blacklistPhoneNumbers: { phoneNumber: string }[] }) => {
			return updateSystemSettings(locationId, input, "BLACK_LIST_PHONE_NUMBERS");
		},
		onSuccess: () => {
			setToDeletePhoneNumbers([]);
		},
		onError: (err) => {
			errorLogger(err);
			setToDeletePhoneNumbers([]);
		},
	});

	const handleChangeCountry = (value: TCountrySelected) => {
		setCountrySelected(value);
	};

	const clearNumbers = (value: string) => {
		setNumbers(value);
		setError(false);
	};

	const handleInputChange = (value: string, mask = "(000) 000-0000") => {
		const transformedMask = convertStringMaskToRegexArray(mask);
		const splitNumbers = value.split(",").map((number) => number.trim());
		const maskedNumbers = splitNumbers.map(
			(number) =>
				conformToMask(number, transformedMask, { guide: false, backSpacePressed: pressedKey === "Backspace" }).conformedValue
		);
		setNumbers(maskedNumbers.join(","));
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const { key } = event;
		setPressedKey(key);
	};

	const handleClickAddButton = () => {
		// Just save if there is no error in order to save the number(s)
		if (!error && numbers !== "") {
			const newNumbers = numbers.split(",").map((number) => number.trim());
			const uniqueNumbers = [...new Set(newNumbers)];
			const specialNumbers = uniqueNumbers.filter((num) => num.includes("*"));
			const cleanedCommingNumbers = clearPhonesNumbers(uniqueNumbers, specialNumbers);
			const input = createMutationInput(cleanedCommingNumbers, phoneNumbers, countrySelected?.code);
			mutateAddPhoneNumbers(input);
			setNumbers("");
			setCountrySelected(null);
		}
	};

	const handleRemovePhoneNumber = (phoneObjects: { phoneNumber: string }[]) => {
		const mappedPhoneNumbers = phoneObjects.map((phone) => phone.phoneNumber);
		setToDeletePhoneNumbers([...toDeletePhoneNumbers, ...mappedPhoneNumbers]);
		const filteredNumbers = phoneNumbers.filter((phone) => !mappedPhoneNumbers.includes(phone.phoneNumber));
		const input = {
			blacklistPhoneNumbers: filteredNumbers,
		};
		mutateDeletePhoneNumber(input);
	};

	const handleStateDialog = (state = false) => {
		setOpenDialog(state);
		setCountrySelected(null);
	};

	useEffect(() => {
		setError(numbers.length !== 0 ? !validatePhoneNumbers(numbers, countrySelected?.mask) : false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [numbers]);

	const eleven = 11;
	const twelve = 12;
	const sliceLimit = isPending && phoneNumbers.length > twelve ? eleven : twelve;

	return (
		<>
			<Grid container direction={"column"} spacing={1}>
				<Grid item>
					<Title title="Activity Blacklist" className="!mb-[24px]" />
				</Grid>
				<Grid item className="!mb-[24px]">
					<PhoneNumbersFormArea
						locationId={locationId}
						disabled={isPending}
						numbers={numbers}
						countrySelected={countrySelected}
						onChangeCountry={handleChangeCountry}
						clearNumbers={clearNumbers}
						handleInputChange={handleInputChange}
						onKeyDown={handleKeyDown}
						onAddButtonClick={handleClickAddButton}
						error={error}
					/>
				</Grid>
				<Grid item container spacing={2} className="!mb-[24px]">
					{isPending && (
						<Grid item xs={3}>
							<ActionCard
								title={
									<Typography className={"regular-pt-sans-pro-narrow-label bold !text-[11pt]"} component={"div"} noWrap>
										Phone Number
									</Typography>
								}
								classes={"action-card-small-padding action-card-br-10 !w-[230px] !h-[37px]"}
								action={
									<SimpleDoubleChecking
										className={"btn-delete"}
										color={color}
										confirmationButtonColor={color}
										icon={<CloseRoundedIcon fontSize="small" />}
										iconHover={<CloseRoundedIcon fontSize="small" />}
										confirmationIcon={
											<SvgIcon viewBox="0 0 20 20" className="!text-[20px] ml-[1px]" component={TrashIcon} />
										}
										disabled={Boolean(isPending)}
										onDeleteAction={() => {}}
									/>
								}
								loading
							/>
						</Grid>
					)}
					{phoneNumbers.slice(0, sliceLimit).map((phoneObject) => {
						return (
							<Grid item xs={3} key={phoneObject._id}>
								<ActionCard
									title={
										<Typography
											className={"regular-pt-sans-pro-narrow-label bold !text-[11pt]"}
											component={"div"}
											noWrap
										>
											{phoneObject.phoneNumber}
										</Typography>
									}
									classes={"action-card-small-padding action-card-br-10 !w-[230px] !h-[37px]"}
									action={
										<SimpleDoubleChecking
											className={"btn-delete"}
											color={color}
											confirmationButtonColor={color}
											icon={<CloseRoundedIcon fontSize="small" />}
											iconHover={<CloseRoundedIcon fontSize="small" />}
											confirmationIcon={
												<SvgIcon viewBox="0 0 20 20" className="!text-[20px] ml-[1px]" component={TrashIcon} />
											}
											disabled={Boolean(isPending)}
											onDeleteAction={() => handleRemovePhoneNumber([phoneObject])}
										/>
									}
									loading={toDeletePhoneNumbers.includes(phoneObject.phoneNumber) && isDeleting}
								/>
							</Grid>
						);
					})}
				</Grid>
				<Grid item alignSelf={"center"}>
					<Button
						sx={{ textTransform: "none", padding: 0 }}
						onClick={() => handleStateDialog(true)}
						disableRipple
						disableFocusRipple
						disableTouchRipple
						disableElevation
					>
						<Typography className="regular-pt-sans-pro-narrow-label bold !text-[16px]" sx={{ textDecoration: "underline" }}>
							View All
						</Typography>
					</Button>
				</Grid>
			</Grid>
			<CallActivityBlackListDialog
				phoneNumbers={phoneNumbers}
				openDialog={openDialog}
				locationId={locationId}
				disabled={isPending}
				numbers={numbers}
				countrySelected={countrySelected}
				onChangeCountry={handleChangeCountry}
				clearNumbers={clearNumbers}
				handleInputChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onAddButtonClick={handleClickAddButton}
				onRemovePhoneNumber={handleRemovePhoneNumber}
				error={error}
				onCloseDialog={() => handleStateDialog(false)}
				isDeleting={isDeleting}
				toDeletePhoneNumbers={toDeletePhoneNumbers}
			/>
		</>
	);
};

export default CallActivityBlackList;
